import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Download,
  FileText,
  Folder,
  HardDrive,
  Shield,
  X,
  Plus,
  Scissors,
  Copy,
  Share2,
  ArrowUpDown,
  Grid3x3
} from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { normalizePath, getParentPath, joinPath, getFileName } from '../utils/pathUtils';
import { Windows11IconMap } from '../simulation/Windows11Icons';

const FileExplorerContent = ({ scenario, onComplete, initialPath, fileSystem: externalFileSystem, setFileSystem: externalSetFileSystem,
  onExecuteFile, onDeleteFile, onOpenFolder, onQuarantine }) => {
  const [internalFileSystem, setInternalFileSystem] = useState([]);
  const [currentPath, setCurrentPath] = useState(initialPath || 'This PC');
  const [selectedItem, setSelectedItem] = useState(null);
  const [history, setHistory] = useState([initialPath || 'This PC']);
  const [historyIndex, setHistoryIndex] = useState(0);
  const explorerOpenedRef = useRef(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [propertiesItem, setPropertiesItem] = useState(null);
  const [enableContentDialog, setEnableContentDialog] = useState(null);

  // Use external file system if provided, otherwise use internal
  const fileSystem = externalFileSystem || internalFileSystem;
  const setFileSystem = externalSetFileSystem || setInternalFileSystem;

  const totalThreats = useRef(0);
  const threatsFound = useRef(0);
  const hasInitialized = useRef(false);


  useEffect(() => {
    // Prevent re-initialization if already done
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    // If using external file system from OS, only add scenario files to it
    if (externalFileSystem && externalSetFileSystem) {
      console.log('🔍 FileExplorerContent using OS file system, adding scenario files only');

      if (scenario?.fileSystem) {
        console.log('✅ Adding scenario files to existing file system:', scenario.fileSystem);
        totalThreats.current = scenario.fileSystem.filter(f => f.isSuspicious).length;

        const addMissingDirs = (path, fs) => {
          if (!path || path === 'This PC') return;
          const normalizedPath = normalizePath(path);
          if (!normalizedPath || normalizedPath === 'This PC') return;

          const parts = normalizedPath.split('\\').filter(Boolean);
          if (parts.length === 0) return;

          let current = parts[0];
          if (/^[A-Za-z]:$/.test(current)) {
            current = `${current}\\`;
          }

          for (let i = 1; i < parts.length; i++) {
            const parentPath = getParentPath(current);
            current = joinPath(current, parts[i]);

            if (!externalFileSystem.some(f => f.path === current) && !fs.some(f => f.path === current)) {
              fs.push({
                id: `dir-${current.replace(/[\\:]/g, '-')}`,
                name: parts[i],
                type: 'folder',
                path: current,
                parentPath: parentPath,
                suspicious: false,
                modified: new Date().toLocaleDateString(),
                size: ''
              });
            }
          }
        };

        let scenarioFS = [];
        scenario.fileSystem.forEach((file, index) => {
          const parentPath = normalizePath(file.path);
          addMissingDirs(parentPath, scenarioFS);
          scenarioFS.push({
            id: `scenario-${file.id || index}`, name: file.name, type: file.type || 'file',
            path: joinPath(parentPath, file.name), parentPath: parentPath, suspicious: file.isSuspicious,
            description: file.description, size: file.size || '1 KB', modified: file.modified || new Date().toLocaleDateString(),
            hash: file.hash,
            signature: file.signature,
            processConfig: file.processConfig || null
          });
        });

        // Add scenario files to existing OS file system
        const combined = [...externalFileSystem, ...scenarioFS];
        const uniqueFS = Array.from(new Map(combined.map(item => [item.path, item])).values());
        externalSetFileSystem(uniqueFS);
      }

      if (initialPath && initialPath !== currentPath) {
        setCurrentPath(normalizePath(initialPath));
        setHistory([normalizePath(initialPath)]);
        setHistoryIndex(0);
      } else if (scenario?.fileSystem?.[0]) {
        const initialScenarioPath = normalizePath(scenario.fileSystem[0].path);
        setCurrentPath(initialScenarioPath);
        setHistory([initialScenarioPath]);
        setHistoryIndex(0);
      }

      return; // Exit early when using external file system
    }

    // Otherwise, initialize internal file system (standalone mode)
    console.log('🔍 FileExplorerContent initializing internal file system');

    const baseFS = [
      { id: 'this-pc', name: 'This PC', type: 'computer', path: 'This PC', parentPath: null },
      { id: 'c-drive', name: 'Local Disk (C:)', type: 'drive', path: 'C:\\', parentPath: 'This PC' },
      { id: 'e-drive', name: 'Removable Disk (E:)', type: 'drive', path: 'E:\\', parentPath: 'This PC' },
      { id: 'c-users', name: 'Users', type: 'folder', path: 'C:\\Users', parentPath: 'C:\\' },
      { id: 'c-windows', name: 'Windows', type: 'folder', path: 'C:\\Windows', parentPath: 'C:\\' },
      { id: 'c-program', name: 'Program Files', type: 'folder', path: 'C:\\Program Files', parentPath: 'C:\\' },
      { id: 'c-users-john', name: 'John Anderson', type: 'folder', path: 'C:\\Users\\John Anderson', parentPath: 'C:\\Users' },
      { id: 'c-users-john-docs', name: 'Documents', type: 'folder', path: 'C:\\Users\\John Anderson\\Documents', parentPath: 'C:\\Users\\John Anderson' },
      { id: 'c-users-john-downloads', name: 'Downloads', type: 'folder', path: 'C:\\Users\\John Anderson\\Downloads', parentPath: 'C:\\Users\\John Anderson' },
      { id: 'c-users-john-docs-report', name: 'Report.docx', type: 'file', size: '1.2 MB', modified: '2024-07-20', path: 'C:\\Users\\John Anderson\\Documents\\Report.docx', parentPath: 'C:\\Users\\John Anderson\\Documents', suspicious: false },
      { id: 'c-windows-logs', name: 'Logs', type: 'folder', path: 'C:\\Windows\\Logs', parentPath: 'C:\\Windows' },
      { id: 'c-users-john-appdata', name: 'AppData', type: 'folder', path: 'C:\\Users\\John Anderson\\AppData', parentPath: 'C:\\Users\\John Anderson' },
      { id: 'c-users-john-appdata-local', name: 'Local', type: 'folder', path: 'C:\\Users\\John Anderson\\AppData\\Local', parentPath: 'C:\\Users\\John Anderson\\AppData' },
      { id: 'c-users-john-appdata-local-temp', name: 'Temp', type: 'folder', path: 'C:\\Users\\John Anderson\\AppData\\Local\\Temp', parentPath: 'C:\\Users\\John Anderson\\AppData\\Local' },
      { id: 'c-temp', name: 'Temp', type: 'folder', path: 'C:\\Temp', parentPath: 'C:\\' },
    ];

    let scenarioFS = [];
    console.log('🔍 FileExplorerContent received scenario:', scenario);
    if (scenario?.fileSystem) {
      console.log('✅ Found fileSystem in scenario:', scenario.fileSystem);
      totalThreats.current = scenario.fileSystem.filter(f => f.isSuspicious).length;

      const addMissingDirs = (path, fs) => {
        if (!path || path === 'This PC') return;
        const normalizedPath = normalizePath(path);
        if (!normalizedPath || normalizedPath === 'This PC') return;

        const parts = normalizedPath.split('\\').filter(Boolean);
        if (parts.length === 0) return;

        let current = parts[0];
        if (/^[A-Za-z]:$/.test(current)) {
          current = `${current}\\`;
        }

        for (let i = 1; i < parts.length; i++) {
          const parentPath = getParentPath(current);
          current = joinPath(current, parts[i]);

          if (!baseFS.some(f => f.path === current) && !fs.some(f => f.path === current)) {
            fs.push({
              id: `dir-${current.replace(/[\\:]/g, '-')}`,
              name: parts[i],
              type: 'folder',
              path: current,
              parentPath: parentPath,
              suspicious: false,
              modified: new Date().toLocaleDateString(),
              size: ''
            });
          }
        }
      };

      scenario.fileSystem.forEach((file, index) => {
        const parentPath = normalizePath(file.path);
        addMissingDirs(parentPath, scenarioFS);
        scenarioFS.push({
          id: `scenario-${file.id || index}`, name: file.name, type: file.type || 'file',
          path: joinPath(parentPath, file.name), parentPath: parentPath, suspicious: file.isSuspicious,
          description: file.description, size: file.size || '1 KB', modified: file.modified || new Date().toLocaleDateString(),
          hash: file.hash,
          signature: file.signature,
          processConfig: file.processConfig || null
        });
      });
    }

    const combined = [...baseFS, ...scenarioFS];
    const uniqueFS = Array.from(new Map(combined.map(item => [item.path, item])).values());
    setInternalFileSystem(uniqueFS);

    if (initialPath && initialPath !== currentPath) {
      setCurrentPath(normalizePath(initialPath));
      setHistory([normalizePath(initialPath)]);
      setHistoryIndex(0);
    } else if (scenario?.fileSystem?.[0]) {
      const initialScenarioPath = normalizePath(scenario.fileSystem[0].path);
      setCurrentPath(initialScenarioPath);
      setHistory([initialScenarioPath]);
      setHistoryIndex(0);
    }
  }, [scenario, initialPath]); // Removed externalFileSystem to prevent re-initialization

  const emitFolderEvent = useCallback((path, options = {}) => {
    if (!path) return;
    const normalizedPath = normalizePath(path);
    const source = options.source || 'navigation';
    const folderItem = options.folderItem || {
      id: options.id || `folder-${normalizedPath}`,
      name: getFileName(normalizedPath) || normalizedPath.split('\\').filter(Boolean).pop() || normalizedPath,
      path: normalizedPath,
      type: 'folder',
      source
    };

    if (onOpenFolder) {
      onOpenFolder(folderItem);
    }

    if (typeof onComplete === 'function') {
      onComplete('folder_opened', {
        path: normalizedPath,
        source,
        folder: folderItem
      });
    }
  }, [onOpenFolder, onComplete]);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const navigateTo = (path, options = {}) => {
    const normalizedPath = normalizePath(path);
    setCurrentPath(normalizedPath);
    setSelectedItem(null);
    const newHistory = history.slice(0, historyIndex + 1);
    if (newHistory[newHistory.length - 1] !== normalizedPath) {
      newHistory.push(normalizedPath);
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    }
    emitFolderEvent(normalizedPath, options);
  };

  const goBack = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const previousPath = history[newIndex];
      setHistoryIndex(newIndex);
      setCurrentPath(previousPath);
      setSelectedItem(null);
      emitFolderEvent(previousPath, { source: 'history_back' });
    }
  };

  const goForward = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      const nextPath = history[newIndex];
      setHistoryIndex(newIndex);
      setCurrentPath(nextPath);
      setSelectedItem(null);
      emitFolderEvent(nextPath, { source: 'history_forward' });
    }
  };

  const navigateUp = () => {
    if (currentPath === 'This PC') return;
    const parent = getParentPath(currentPath);
    if (parent) navigateTo(parent, { source: 'navigate_up' });
  };

  const getCurrentDirectoryItems = () => {
    const normalizedCurrentPath = normalizePath(currentPath);
    return fileSystem.filter(item => {
      const normalizedParentPath = normalizePath(item.parentPath || '');
      // Also check if the item's path starts with currentPath (for files that might have full path)
      const normalizedItemPath = normalizePath(item.path || '');
      return normalizedParentPath === normalizedCurrentPath ||
        (normalizedItemPath && normalizedItemPath.startsWith(normalizedCurrentPath + '\\'));
    });
  };

  const handleItemDoubleClick = (item) => {
    if (item.type === 'folder') {
      const destination = item.path || `${currentPath}\\${item.name}`;
      navigateTo(destination, { source: 'double_click', folderItem: item });
    } else if (item.name && item.name.toLowerCase().endsWith('.zip')) {
      // Double-clicking ZIP file extracts it
      handleExtractZip(item);
    } else if ((item.name && item.name.toLowerCase().endsWith('.exe')) || item.processConfig || item.type === 'executable') {
      if (onExecuteFile) {
        console.log('[FileExplorer] Executing file:', item.name);
        onExecuteFile(item);
      }
      // Also emit file_executed event for step validation
      if (typeof onComplete === 'function') {
        onComplete('file_executed', {
          id: item.id,
          name: item.name,
          path: item.path,
          type: item.type,
          suspicious: item.suspicious || item.isSuspicious,
          processConfig: item.processConfig
        });
      }
    } else if (item.name && (item.name.toLowerCase().endsWith('.docm') || item.name.toLowerCase().endsWith('.doc'))) {
      // For macro-enabled documents, show "Enable Content" dialog
      setEnableContentDialog(item);
      // Also emit file_opened event
      if (typeof onComplete === 'function') {
        onComplete('file_opened', {
          id: item.id,
          name: item.name,
          path: item.path,
          type: item.type,
          suspicious: item.suspicious || item.isSuspicious
        });
      }
    } else {
      // For other files, emit file_opened event for step tracking
      if (typeof onComplete === 'function') {
        onComplete('file_opened', {
          id: item.id,
          name: item.name,
          path: item.path,
          type: item.type,
          suspicious: item.suspicious || item.isSuspicious
        });
      }
    }
  };

  const handleQuarantine = async (itemToQuarantine) => {
    let quarantineHandled = false;
    let completionPayload = null;
    let handledEventName = null;
    let shouldIncrementThreats = true;

    const finalizeQuarantine = (eventName, payload, suppressDefaultEvent = false) => {
      if (!suppressDefaultEvent && typeof onComplete === 'function') {
        onComplete(eventName, payload);
      }

      setFileSystem(fs => fs.filter(f => f.id !== itemToQuarantine.id));
      setSelectedItem(null);
      threatsFound.current += 1;

      if (threatsFound.current >= totalThreats.current && totalThreats.current > 0) {
        if (typeof onComplete === 'function') {
          onComplete('file_quarantine_summary', {
            success: true,
            message: 'All threats neutralized.',
            correct: threatsFound.current,
            total: totalThreats.current,
            score: Math.round((threatsFound.current / totalThreats.current) * 100)
          });
        }
      }
    };

    const isAdsStream = itemToQuarantine?.type === 'ads_stream' || (itemToQuarantine?.name || '').includes(':');
    const defaultEventName = isAdsStream ? 'ads_quarantined' : 'file_quarantined';

    let resultPayload = null;
    let suppressDefault = false;

    if (onQuarantine) {
      try {
        const result = onQuarantine(itemToQuarantine);
        if (result && typeof result.then === 'function') {
          const awaited = await result;
          if (awaited) {
            suppressDefault = !!awaited.handledEvent;
            resultPayload = awaited.payload || null;
          }
        } else if (result) {
          suppressDefault = !!result.handledEvent;
          resultPayload = result.payload || null;
        }
      } catch (error) {
        console.error('[FileExplorer] onQuarantine handler failed:', error);
      }
    }

    if (resultPayload) {
      finalizeQuarantine(resultPayload.event || defaultEventName, resultPayload.payload || resultPayload, suppressDefault);
    } else {
      finalizeQuarantine(defaultEventName, {
        file: {
          id: itemToQuarantine.id,
          name: itemToQuarantine.name,
          path: itemToQuarantine.path,
          size: itemToQuarantine.size,
          description: itemToQuarantine.description,
          suspicious: itemToQuarantine.suspicious || itemToQuarantine.isSuspicious,
          type: itemToQuarantine.type
        },
        adsPath: isAdsStream ? itemToQuarantine.path : undefined,
        processTerminated: !isAdsStream
      }, suppressDefault);
    }

    if (threatsFound.current >= totalThreats.current && totalThreats.current > 0) {
      if (typeof onComplete === 'function') {
        onComplete('file_quarantine_summary', {
          success: true,
          message: 'All threats neutralized.',
          correct: threatsFound.current,
          total: totalThreats.current,
          score: Math.round((threatsFound.current / totalThreats.current) * 100)
        });
      }
    }
  };

  const items = getCurrentDirectoryItems();

  useEffect(() => {
    const handleGlobalClick = (event) => {
      if (contextMenu && event.type === 'click') {
        closeContextMenu();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (contextMenu) {
          closeContextMenu();
        }
        if (propertiesItem) {
          setPropertiesItem(null);
        }
      }
    };

    if (contextMenu) {
      document.addEventListener('click', handleGlobalClick);
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      if (contextMenu) {
        document.removeEventListener('click', handleGlobalClick);
      }
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [contextMenu, closeContextMenu, propertiesItem]);

  const handleContextMenu = (event, item) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedItem(item);
    setContextMenu({
      x: event.clientX,
      y: event.clientY,
      item
    });
  };

  const handleShowProperties = (item) => {
    setPropertiesItem(item);
    if (typeof onComplete === 'function') {
      onComplete('file_properties_viewed', {
        id: item.id,
        name: item.name,
        path: item.path,
        parentPath: item.parentPath,
        size: item.size,
        modified: item.modified,
        suspicious: item.suspicious || item.isSuspicious,
        hash: item.hash,
        signature: item.signature,
        type: item.type
      });
    }
  };

  const handleEnableContent = (item) => {
    setEnableContentDialog(null);

    // Emit macro_enabled event
    if (typeof onComplete === 'function') {
      onComplete('macro_enabled', {
        id: item.id,
        name: item.name,
        path: item.path,
        type: item.type,
        suspicious: item.suspicious || item.isSuspicious
      });
    }

    // If file has processConfig, also execute it
    if (item.processConfig && onExecuteFile) {
      console.log('[FileExplorer] Executing macro from file:', item.name);
      onExecuteFile(item);
    }
  };

  const handleExtractZip = (zipItem) => {
    console.log('[FileExplorer] Extracting ZIP file:', zipItem.name);

    // Determine extraction directory (same as ZIP file location)
    const zipParentPath = zipItem.parentPath || getParentPath(zipItem.path) || currentPath;
    const normalizedParentPath = normalizePath(zipParentPath);

    // Check if this is Invoice_7823.zip - extract Invoice_7823.docx.exe
    const zipName = zipItem.name.toLowerCase();
    if (zipName.includes('invoice_7823') || zipName.includes('invoice')) {
      // Extract Invoice_7823.docx.exe
      const extractedFileName = 'Invoice_7823.docx.exe';
      const extractedFilePath = joinPath(normalizedParentPath, extractedFileName);

      // Check if file already exists
      const existingFile = fileSystem.find(f =>
        normalizePath(f.path) === normalizePath(extractedFilePath)
      );

      if (!existingFile) {
        // Find the extracted file in scenario.fileSystem to get its processConfig
        let extractedFileConfig = null;
        if (scenario?.fileSystem) {
          extractedFileConfig = scenario.fileSystem.find(f =>
            f.name && f.name.toLowerCase() === extractedFileName.toLowerCase()
          );
        }

        // If processConfig not found, try to match from backgroundProcesses
        let processConfig = extractedFileConfig?.processConfig || null;
        if (!processConfig && scenario?.backgroundProcesses) {
          const fileNameLower = extractedFileName.toLowerCase();
          const matchingProcess = scenario.backgroundProcesses.find(proc => {
            if (!proc.cmdline) return false;
            const cmdlineLower = proc.cmdline.toLowerCase();
            return cmdlineLower.includes(fileNameLower) ||
              cmdlineLower.includes(fileNameLower.replace('.exe', ''));
          });

          if (matchingProcess) {
            processConfig = {
              name: matchingProcess.name || extractedFileName,
              cpu: matchingProcess.cpu || { mean: 0, jitter: 0 },
              memory: matchingProcess.memory || { mean: 0, jitter: 0 },
              disk: matchingProcess.disk || { mean: 0, jitter: 0 },
              network: matchingProcess.network || { mean: 0, jitter: 0 },
              isRogue: matchingProcess.isRogue !== false,
              cmdline: matchingProcess.cmdline,
              description: matchingProcess.description
            };
          }
        }

        // Create the extracted file
        const extractedFile = {
          id: `extracted-${Date.now()}-${extractedFileName.replace(/[^a-z0-9]/gi, '-')}`,
          name: extractedFileName,
          type: 'file',
          path: extractedFilePath,
          parentPath: normalizedParentPath,
          size: extractedFileConfig?.size || '256 KB',
          modified: new Date().toLocaleDateString(),
          description: extractedFileConfig?.description || 'Extracted executable file',
          suspicious: extractedFileConfig?.isSuspicious !== false, // Default to suspicious
          isSuspicious: extractedFileConfig?.isSuspicious !== false,
          processConfig: processConfig,
          hash: extractedFileConfig?.hash,
          signature: extractedFileConfig?.signature,
          autoGenerated: false
        };

        // Add to file system
        setFileSystem(prev => {
          const updated = [...prev, extractedFile];
          return Array.from(new Map(updated.map(item => [item.path, item])).values());
        });

        console.log('[FileExplorer] Extracted file added:', extractedFile);

        // Emit file_extracted event
        if (typeof onComplete === 'function') {
          onComplete('file_extracted', {
            zipFile: zipItem,
            extractedFile: extractedFile,
            extractionPath: normalizedParentPath
          });
        }
      } else {
        console.log('[FileExplorer] File already exists:', extractedFileName);
      }
    } else {
      // Generic ZIP extraction - just emit event
      if (typeof onComplete === 'function') {
        onComplete('file_extracted', {
          zipFile: zipItem,
          extractionPath: normalizedParentPath
        });
      }
    }
  };

  const handleContextMenuAction = (action, item) => {
    switch (action) {
      case 'open':
        if (item.type === 'folder') {
          const destination = item.path || `${currentPath}\\${item.name}`;
          navigateTo(destination, { source: 'context_menu', folderItem: item });
        } else {
          // For files, use the same handler as double-click
          handleItemDoubleClick(item);
        }
        break;
      case 'extract':
        handleExtractZip(item);
        break;
      case 'enable_content':
        // Show Enable Content dialog for macro-enabled documents
        setEnableContentDialog(item);
        break;
      case 'quarantine':
        handleQuarantine(item);
        break;
      case 'properties':
        handleShowProperties(item);
        break;
      default:
        break;
    }

    closeContextMenu();
  };

  const buildContextMenuItems = (item) => {
    if (!item) return [];

    const entries = [
      { key: 'open', label: item.type === 'folder' ? 'Open' : 'Open' },
    ];

    // Add "Extract" for ZIP files
    if (item.name && item.name.toLowerCase().endsWith('.zip')) {
      entries.push({ key: 'extract', label: 'Extract All...' });
    }

    // Add "Enable Content" for macro-enabled documents
    if (item.name && (item.name.toLowerCase().endsWith('.docm') || item.name.toLowerCase().endsWith('.doc'))) {
      entries.push({ key: 'enable_content', label: 'Enable Content' });
    }

    if (item.suspicious) {
      entries.push({ key: 'quarantine', label: 'Quarantine' });
    }

    entries.push({ key: 'properties', label: 'Properties' });

    return entries;
  };

  useEffect(() => {
    if (!explorerOpenedRef.current && currentPath) {
      explorerOpenedRef.current = true;
      if (typeof onComplete === 'function') {
        onComplete('file_explorer_opened', {
          path: currentPath
        });
      }
    }
  }, [currentPath, onComplete]);

  // Build path segments for address bar
  const getPathSegments = () => {
    if (currentPath === 'This PC') return [{ name: 'This PC', path: 'This PC', icon: 'mycomputer' }];

    const segments = [];
    const normalizedPath = normalizePath(currentPath);
    const parts = normalizedPath.split('\\').filter(Boolean);

    // Add "This PC" as first segment
    segments.push({ name: 'This PC', path: 'This PC', icon: 'mycomputer' });

    // Build path progressively
    let currentPathBuilt = '';
    parts.forEach((part, index) => {
      if (index === 0 && /^[A-Za-z]:$/.test(part)) {
        currentPathBuilt = `${part}\\`;
        segments.push({ name: `Local Disk (${part})`, path: currentPathBuilt, icon: 'drive' });
      } else if (index === 0 && part.includes(':')) {
        currentPathBuilt = part;
        segments.push({ name: part, path: currentPathBuilt, icon: 'drive' });
      } else {
        currentPathBuilt = currentPathBuilt ? joinPath(currentPathBuilt, part) : `${part}\\`;
        segments.push({ name: part, path: currentPathBuilt, icon: 'folder' });
      }
    });

    return segments;
  };

  const pathSegments = getPathSegments();
  const FileExplorerIcon = Windows11IconMap['explorer'] || Folder;
  const ThisPCIcon = Windows11IconMap['mycomputer'] || HardDrive;
  const DocumentsIcon = Windows11IconMap['documents'] || Folder;

  return (
    <div className="h-full flex flex-col bg-white text-black">
      {/* Windows 11 Style Ribbon Menu */}
      <div className="flex items-center px-3 py-2 border-b border-gray-200 bg-white space-x-4">
        {/* New Group */}
        <div className="flex items-center space-x-2 border-r border-gray-300 pr-4">
          <button className="flex flex-col items-center p-2 hover:bg-gray-100 rounded transition-colors">
            <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center mb-1">
              <Plus size={16} className="text-white" />
            </div>
            <span className="text-xs">New</span>
          </button>
        </div>

        {/* Clipboard/File Operations Group */}
        <div className="flex items-center space-x-2 border-r border-gray-300 pr-4">
          <button className="p-2 hover:bg-gray-100 rounded transition-colors" title="Cut">
            <Scissors size={18} className="text-blue-600" />
          </button>
          <button className="p-2 hover:bg-gray-100 rounded transition-colors" title="Copy">
            <Copy size={18} className="text-blue-600" />
          </button>
          <button className="p-2 hover:bg-gray-100 rounded transition-colors" title="Paste">
            <div className="relative">
              <FileText size={18} className="text-blue-600" />
              <FileText size={14} className="text-blue-600 absolute -bottom-1 -right-1 opacity-60" />
            </div>
          </button>
          <button className="p-2 hover:bg-gray-100 rounded transition-colors" title="Move To">
            <div className="relative">
              <FileText size={18} className="text-blue-600" />
              <ChevronRight size={12} className="text-blue-600 absolute -bottom-0.5 -right-1" />
            </div>
          </button>
          <button className="p-2 hover:bg-gray-100 rounded transition-colors" title="Share">
            <Share2 size={18} className="text-blue-600" />
          </button>
        </div>

        {/* Organization Group */}
        <div className="flex items-center space-x-2">
          <button className="flex flex-col items-center p-2 hover:bg-gray-100 rounded transition-colors">
            <ArrowUpDown size={18} className="text-gray-700 mb-1" />
            <span className="text-xs">Sort</span>
          </button>
          <button className="flex flex-col items-center p-2 hover:bg-gray-100 rounded transition-colors">
            <Grid3x3 size={18} className="text-gray-700 mb-1" />
            <span className="text-xs">View</span>
          </button>
        </div>
      </div>

      {/* Windows 11 Style Address Bar */}
      <div className="flex items-center px-3 py-2 border-b border-gray-200 bg-white space-x-2">
        {/* Navigation Arrows */}
        <button
          onClick={goBack}
          disabled={historyIndex === 0}
          className="p-1.5 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          title="Back"
        >
          <ChevronLeft size={18} className="text-gray-700" />
        </button>
        <button
          onClick={goForward}
          disabled={historyIndex === history.length - 1}
          className="p-1.5 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          title="Forward"
        >
          <ChevronRight size={18} className="text-gray-700" />
        </button>
        <button
          onClick={navigateUp}
          className="p-1.5 hover:bg-gray-100 rounded transition-colors"
          title="Up"
        >
          <ChevronUp size={18} className="text-gray-700" />
        </button>

        {/* Path Display with Icons */}
        <div className="flex-1 flex items-center bg-gray-50 rounded px-3 py-1.5 border border-gray-200">
          {pathSegments.map((segment, index) => {
            const SegmentIcon = segment.icon === 'mycomputer'
              ? ThisPCIcon
              : segment.icon === 'drive'
                ? HardDrive
                : Folder;

            return (
              <React.Fragment key={index}>
                {index > 0 && (
                  <span className="mx-2 text-gray-400 text-sm">›</span>
                )}
                <button
                  onClick={() => navigateTo(segment.path, { source: 'address_bar' })}
                  className="flex items-center space-x-1.5 px-2 py-0.5 hover:bg-gray-200 rounded transition-colors group"
                >
                  <SegmentIcon size={16} />
                  <span className="text-sm text-gray-700 group-hover:text-gray-900">{segment.name}</span>
                </button>
              </React.Fragment>
            );
          })}
        </div>
      </div>
      <div className="flex flex-1 overflow-hidden">
        {/* Quick Access Sidebar */}
        <div className="hidden md:block w-48 border-r border-gray-200 p-2 overflow-y-auto bg-gray-50">
          <div className="space-y-1">
            <div
              onClick={() => navigateTo('This PC', { source: 'quick_access' })}
              className="flex items-center p-2 hover:bg-gray-200 rounded cursor-pointer transition-colors"
            >
              <ThisPCIcon size={20} />
              <span className="text-sm ml-2">This PC</span>
            </div>
            <div
              onClick={() => navigateTo('C:\\Users\\John Anderson\\Documents', { source: 'quick_access' })}
              className="flex items-center p-2 hover:bg-gray-200 rounded cursor-pointer transition-colors"
            >
              <DocumentsIcon size={20} />
              <span className="text-sm ml-2">Documents</span>
            </div>
            <div
              onClick={() => navigateTo('C:\\Users\\John Anderson\\Downloads', { source: 'quick_access' })}
              className="flex items-center p-2 hover:bg-gray-200 rounded cursor-pointer transition-colors"
            >
              <Download size={20} className="text-blue-600" />
              <span className="text-sm ml-2">Downloads</span>
            </div>
            <div
              onClick={() => navigateTo('C:\\Temp', { source: 'quick_access' })}
              className="flex items-center p-2 hover:bg-gray-200 rounded cursor-pointer transition-colors"
            >
              <Folder size={20} className="text-blue-600" />
              <span className="text-sm ml-2">Temp</span>
            </div>
          </div>
        </div>
        <div className="flex-1 flex flex-col">
          <div className="p-2 border-b border-gray-200 grid grid-cols-12 gap-2">
            <span className="text-sm font-medium col-span-6">Name</span>
            <span className="hidden sm:block text-sm text-gray-500 col-span-3">Date modified</span>
            <span className="hidden sm:block text-sm text-gray-500 col-span-2">Type</span>
            <span className="text-sm text-gray-500 col-span-1 text-right">Size</span>
          </div>
          <div className="flex-1 overflow-y-auto">
            {items.map((item, index) => (
              <div key={item.id || index}
                className={`grid grid-cols-12 gap-2 p-2 hover:bg-blue-50 cursor-pointer ${selectedItem?.id === item.id ? 'bg-blue-100' : ''}`}
                onClick={() => setSelectedItem(item)}
                onDoubleClick={() => handleItemDoubleClick(item)}
                onContextMenu={(event) => handleContextMenu(event, item)}
              >
                <div className="flex items-center col-span-6">
                  {item.type === 'folder' ? <Folder size={16} className="mr-2 text-yellow-500" /> : item.type === 'drive' ? <HardDrive size={16} className="mr-2 text-gray-700" /> : <FileText size={16} className="mr-2 text-gray-500" />}
                  <span className={`text-sm truncate ${item.suspicious ? 'text-red-600 font-medium' : ''}`}>{item.name}</span>
                  {item.suspicious && <AlertTriangle size={14} className="ml-2 text-red-500 flex-shrink-0" title={item.description} />}
                </div>
                <div className="hidden sm:block text-xs text-gray-500 col-span-3">{item.modified}</div>
                <div className="hidden sm:block text-xs text-gray-500 col-span-2">{item.type === 'folder' ? 'Folder' : item.type === 'drive' ? 'Local Disk' : 'File'}</div>
                <div className="text-xs text-gray-500 col-span-1 text-right">{item.size}</div>
              </div>
            ))}
          </div>
        </div>
        {selectedItem && (
          <div className="hidden lg:flex w-64 border-l border-gray-200 p-4 flex-col">
            <div className="flex justify-center mb-4">
              {selectedItem.type === 'folder' ? <Folder size={48} className="text-yellow-500" /> : selectedItem.type === 'drive' ? <HardDrive size={48} className="text-gray-700" /> : <FileText size={48} className="text-gray-500" />}
            </div>
            <h3 className="font-bold text-center truncate">{selectedItem.name}</h3>
            <div className="text-xs text-gray-500 text-center mb-4">{selectedItem.type}</div>
            <div className="text-xs space-y-1 border-t pt-2">
              <p><strong>Size:</strong> {selectedItem.size}</p>
              <p><strong>Modified:</strong> {selectedItem.modified}</p>
              {selectedItem.suspicious && selectedItem.hash && (
                <p><strong>Hash:</strong> <span className="font-mono text-xs">{selectedItem.hash}</span></p>
              )}
              {selectedItem.suspicious && selectedItem.signature && (
                <p><strong>Signature:</strong> <span className="font-mono text-xs text-red-700">{selectedItem.signature}</span></p>
              )}
            </div>
            {selectedItem.suspicious && (
              <div className="mt-4 p-2 bg-red-100 border border-red-300 rounded">
                <div className="flex items-center mb-1"><AlertTriangle size={16} className="text-red-600 mr-2" /><span className="font-bold text-red-800">Threat Detected</span></div>
                <p className="text-red-700 text-xs">{selectedItem.description || "This file exhibits suspicious characteristics."}</p>
                {selectedItem.signature?.toLowerCase().includes('polymorph') && (
                  <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                    <p className="text-yellow-800 text-xs font-semibold">⚠️ Polymorphic Behavior Detected:</p>
                    <ul className="text-yellow-700 text-xs mt-1 space-y-1">
                      <li>• Self-modifying code patterns</li>
                      <li>• Variable file hashes</li>
                      <li>• Code obfuscation detected</li>
                    </ul>
                  </div>
                )}
              </div>
            )}
            <div className="mt-auto pt-4">
              {selectedItem.suspicious && (
                <button onClick={() => handleQuarantine(selectedItem)} className="w-full px-3 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 flex items-center justify-center space-x-2">
                  <Shield size={16} /><span>Quarantine</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
      {contextMenu?.item && (
        <div
          className="fixed bg-white border border-gray-300 rounded shadow-xl py-1 text-sm z-50 w-44"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onContextMenu={(event) => event.preventDefault()}
        >
          {buildContextMenuItems(contextMenu.item).map((option) => (
            <button
              key={option.key}
              onClick={() => handleContextMenuAction(option.key, contextMenu.item)}
              className="w-full text-left px-4 py-2 hover:bg-blue-500 hover:text-white transition-colors"
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
      {propertiesItem && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => setPropertiesItem(null)}
        >
          <div
            className="bg-gray-100 rounded shadow-2xl w-full max-w-lg border-2 border-gray-400"
            onClick={(event) => event.stopPropagation()}
          >
            {/* Simple Header */}
            <div className="flex items-center justify-between px-5 py-2.5 bg-gray-200 border-b-2 border-gray-300 rounded-t">
              <h3 className="text-sm font-bold text-gray-900">Properties</h3>
              <button
                onClick={() => setPropertiesItem(null)}
                className="p-1 text-gray-600 hover:text-gray-800 hover:bg-gray-300 rounded transition-colors"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>

            {/* Content */}
            <div className="px-5 py-4 bg-gray-100 space-y-3 text-sm">
              {/* File Name */}
              <div className="flex items-start gap-2 py-1">
                <span className="font-semibold text-gray-700 whitespace-nowrap">
                  {propertiesItem.type === 'folder' ? 'Folder:' : 'File:'}
                </span>
                <span className="text-gray-900 font-medium break-words">{propertiesItem.name}</span>
              </div>

              {/* Location */}
              <div className="flex items-start gap-2 py-1">
                <span className="font-semibold text-gray-700 whitespace-nowrap">Location:</span>
                <span className="text-gray-900 break-all font-mono text-xs">
                  {propertiesItem.parentPath || getParentPath(propertiesItem.path) || propertiesItem.path}
                </span>
              </div>

              {/* Size */}
              <div className="flex items-start gap-1 py-1">
                <span className="font-semibold text-gray-700 whitespace-nowrap">Size:</span>
                <span className="text-gray-900">{propertiesItem.size || '—'}</span>
              </div>

              {/* Modified Date */}
              {propertiesItem.modified && (
                <div className="flex items-start gap-1 py-1">
                  <span className="font-semibold text-gray-700 whitespace-nowrap">Modified:</span>
                  <span className="text-gray-900">{propertiesItem.modified}</span>
                </div>
              )}

              {/* Details/Description */}
              {propertiesItem.description && (
                <div className="flex items-start gap-2 py-1">
                  <span className="font-semibold text-gray-700 whitespace-nowrap">Details:</span>
                  <span className="text-gray-900 break-words flex-1">{propertiesItem.description}</span>
                </div>
              )}

              {/* Security Metadata (if present) */}
              {(propertiesItem.hash || propertiesItem.signature) && (
                <div className="pt-3 mt-3 border-t border-gray-400 space-y-2">
                  {propertiesItem.hash && (
                    <div className="flex items-start gap-2">
                      <span className="font-semibold text-gray-700 text-xs whitespace-nowrap">Hash:</span>
                      <span className="font-mono text-xs break-all text-gray-900">{propertiesItem.hash}</span>
                    </div>
                  )}
                  {propertiesItem.signature && (
                    <div className="flex items-start gap-2">
                      <span className="font-semibold text-gray-700 text-xs whitespace-nowrap">Signature:</span>
                      <span className="font-mono text-xs break-all text-gray-900">{propertiesItem.signature}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer with Close button */}
            {/* <div className="flex justify-end gap-2 px-5 py-3 bg-gray-200 border-t-2 border-gray-300 rounded-b">
              <button
                onClick={() => setPropertiesItem(null)}
                className="px-5 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-400 rounded hover:bg-gray-50 hover:border-gray-500 transition-colors shadow-sm"
              >
                Close
              </button>
            </div> */}
          </div>
        </div>
      )}
      {/* Enable Content Dialog for Macro-Enabled Documents */}
      {enableContentDialog && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setEnableContentDialog(null)}
        >
          <div
            className="bg-white rounded-lg shadow-2xl w-full max-w-lg border-2 border-orange-400"
            onClick={(event) => event.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 bg-orange-50 border-b-2 border-orange-300 rounded-t">
              <div className="flex items-center gap-3">
                <AlertTriangle size={24} className="text-orange-600" />
                <div>
                  <h3 className="text-lg font-bold text-orange-900">Security Warning</h3>
                  <p className="text-sm text-orange-700">Macros have been disabled</p>
                </div>
              </div>
              <button
                onClick={() => setEnableContentDialog(null)}
                className="p-1 text-orange-600 hover:text-orange-800 hover:bg-orange-200 rounded transition-colors"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-5 bg-white">
              <div className="mb-4">
                <p className="text-gray-800 mb-2">
                  <strong className="font-semibold">File:</strong> {enableContentDialog.name}
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  This document contains macros. Macros might contain viruses and other security hazards.
                  Don't enable this content unless you trust the source of this file.
                </p>
              </div>

              <div className="bg-yellow-50 border border-yellow-300 rounded p-3 mb-4">
                <p className="text-sm text-yellow-800">
                  <strong>⚠️ Warning:</strong> Enabling macros can execute malicious code on your computer.
                </p>
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setEnableContentDialog(null)}
                  className="px-5 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleEnableContent(enableContentDialog)}
                  className="px-5 py-2 text-sm font-medium text-white bg-orange-600 border border-orange-700 rounded hover:bg-orange-700 transition-colors shadow-sm"
                >
                  Enable Content
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="border-t border-gray-200 p-1 text-xs text-gray-500 flex justify-between">
        <span>{items.length} item{items.length !== 1 && 's'}</span>
        {totalThreats.current > 0 && <span>Threats found: {threatsFound.current} / {totalThreats.current}</span>}
        <span>{selectedItem ? `1 item selected` : ''}</span>
      </div>
    </div>
  );
};

export default FileExplorerContent;